import { Config, ExperimentSegments, getExperimentSegment, noticeError } from '@ratehub/base-ui';

import EXPERIMENTS from '../definitions/Experiments';
import APPROVAL_LIKELIHOOD from '../definitions/ApprovalLikelihood';
import { APPROVAL_LIKELIHOOD_SCORES, PRODUCT_TYPES } from '../definitions/CurrentDocument';


/**
 * Maps a raw API approval likelihood value (as found in API response card.approval_likelihood_raw)
 * into an entry within APPROVAL_LIKELIHOOD (strings from 'unknown' through 'prequalified').
 * @param {boolean} isVerified - has the rawLikelihood value been verified from a valid approval likelihood document?
 * @param {number} rawLikelihood - a float from 0..100, or null
 * @param {string[]} [cardTypes=[]] - an array of CC product types for the card in question
 * @returns {string} a value from APPROVAL_LIKELIHOOD, or undefined if rawLikelihood is undefined
 */
function getApprovalLikelihoodFromAPIResponse(
    isVerified,
    rawLikelihood,
    cardTypes = []
) {
    if (!isVerified) {
        return undefined;
    }

    // null or undefined rawLikelihood means unknown
    if (rawLikelihood == null) {
        return APPROVAL_LIKELIHOOD.UNKNOWN;
    }

    // secured cards with ANY rawLikelihood (expecting only 100, btw)
    // should always be considered GOOD approval likelihood
    if (cardTypes.includes(PRODUCT_TYPES.SECURED)) {
        return APPROVAL_LIKELIHOOD.GOOD;
    }

    // Only if the approval model is using VARIANT 1 do we look to the approval threshold experiment.
    // Otherwise, we use segment 0 which is our default threshold values.
    const approvalSegment = getExperimentSegment(EXPERIMENTS.approvalModel) === ExperimentSegments.VARIANT_1
        ? getExperimentSegment(EXPERIMENTS.approvalThreshold)
        : 0;
    const minimumThresholds = getMinimumThresholds(approvalSegment);

    if (rawLikelihood === 100) {
        // TODO Remove Config flag when after CC prequalified is fully enabled
        // until CC recognizes PREQUALIFIED, report them as EXCELLENT instead
        return Config.ENABLE_CC_PREQUALIFIED
            ? APPROVAL_LIKELIHOOD_SCORES.PREQUALIFIED
            : APPROVAL_LIKELIHOOD_SCORES.EXCELLENT;

    } else if (rawLikelihood >= minimumThresholds.excellent) {
        return APPROVAL_LIKELIHOOD_SCORES.EXCELLENT;
    } else if (
        minimumThresholds.veryGood != null // only present in variants
        && rawLikelihood >= minimumThresholds.veryGood
    ) {
        return APPROVAL_LIKELIHOOD_SCORES.VERY_GOOD;
    } else if (minimumThresholds != null && rawLikelihood >= minimumThresholds.good) {
        return APPROVAL_LIKELIHOOD_SCORES.GOOD;
    } else if (rawLikelihood >= minimumThresholds.fair) {
        return APPROVAL_LIKELIHOOD_SCORES.FAIR;
    } else {
        return APPROVAL_LIKELIHOOD_SCORES.POOR;
    }
}

/**
 * Get the array of approval likelihood threshold values.
 *  The MINIMUM_THRESHOLDS array must be defined within this function, otherwise when we change Config
 *  settings as part of our tests, they would not be reflected in the array values.
 *  If it wasn’t for that, this array could be defined external to the function.
 * @param {number} approvalSegment - index into an array of threshold values
 * @returns {Array<{excellent: string, good: string, fair: string}>} - minimum threshold values for excellent, good, and fair
 */
function getMinimumThresholds(approvalSegment) {
    // The experiment segment for `cc-approval-threshold` provides the index to retrieve
    // the minimum threshold values for EXCELLENT, VERY_GOOD, GOOD, and FAIR approval likelihoods.
    const MINIMUM_THRESHOLDS = Config.CC_APPROVAL_LIKELIHOOD_MINIMUMS;
    // by default, CONTROL will be { 69.99, 18, 6 } for { excellent, good, fair } (at least initially)
    // while variants will have an additional threshold { excellent, veryGood, good, fair }

    // get the four thresholds for the given experiment segment
    const minimumThresholds = MINIMUM_THRESHOLDS[approvalSegment];

    if (!minimumThresholds) {
        // return CONTROL, but complain first
        noticeError(new RangeError('[getMinimumThresholds] Invalid index for minimum threshold array; falling back to CONTROL.'), {
            approvalSegment,
            lengthOfThresholdArray: MINIMUM_THRESHOLDS.length,
        });

        return MINIMUM_THRESHOLDS[0];
    } else {
        return minimumThresholds;
    }
}


const APPROVAL_LIKELIHOOD_RANKING = [
    APPROVAL_LIKELIHOOD.UNKNOWN,
    APPROVAL_LIKELIHOOD.POOR,
    APPROVAL_LIKELIHOOD.FAIR,
    APPROVAL_LIKELIHOOD.GOOD,
    APPROVAL_LIKELIHOOD.VERY_GOOD,
    APPROVAL_LIKELIHOOD.EXCELLENT,
    APPROVAL_LIKELIHOOD.PREQUALIFIED,
];

/**
 * Maps an cardInfo APPROVAL_LIKELIHOOD value into a rank number where higher is more likely to be approved
 * @param {string} approvalLikelihood - APPROVAL_LIKELIHOOD value
 * @returns {number} index value of APPROVAL_LIKELIHOOD
 */
function getApprovalLikelihoodRank(approvalLikelihood) {
    return APPROVAL_LIKELIHOOD_RANKING.indexOf(approvalLikelihood);
}


export { getApprovalLikelihoodRank };
export default getApprovalLikelihoodFromAPIResponse;
